import { useCallback } from "react";
import { useDataRequest } from "../../common/hooks/api";
import { NetworkRequest, NetworkRequestState } from "../../common/models";
import { Api } from "../../common/service/Api";
import { PrivacyCenterAvailableIntegrationVendorIdsDto } from "../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { usePrimaryOrganizationId } from "./useOrganization";
import { usePrivacyCenter } from "./usePrivacyCenter";

type UseAvailableIntegrationVendorIds = {
  integrationAvailability: PrivacyCenterAvailableIntegrationVendorIdsDto;
  privacyCenterRequest: NetworkRequestState;
  integrationAvailabilityRequest: NetworkRequestState;
  /**
   * True once there is an answer to work with: availability is loaded (possibly from the cache while it refreshes),
   * its request has settled, or there is no privacy center to ask about. Unlike the request states, this does not
   * wait for refetches of data the page already has.
   */
  isReady: boolean;
};

export const useAvailableIntegrationVendorIds = (
  enabled = true,
  {
    failFast = false,
  }: {
    /**
     * Retry a failed lookup once, after 250ms, instead of react-query's 3 retries with backoff. For the Workflow Setup
     * tables, which would otherwise sit blank for 10s or more. Request steps keep the default: dropping integrations
     * there quietly removes vendors whose data is only reachable through one.
     */
    failFast?: boolean;
  } = {},
): UseAvailableIntegrationVendorIds => {
  const organizationId = usePrimaryOrganizationId();
  const [privacyCenter, privacyCenterRequest] = usePrivacyCenter(organizationId, !enabled);

  const api = useCallback(
    () => Api.privacyCenter.getAvailableIntegrationVendorIds(organizationId, privacyCenter.id),
    [organizationId, privacyCenter?.id],
  );

  const { result: integrationAvailability, request: integrationAvailabilityRequest } =
    useDataRequest({
      queryKey: ["availableIntegrationVendorIds", organizationId, privacyCenter?.id],
      api,
      notReady: !enabled || !organizationId || !privacyCenter?.id,
      retry: failFast ? 1 : undefined,
      retryDelay: failFast ? 250 : undefined,
    });

  const isReady =
    integrationAvailability !== undefined ||
    NetworkRequest.areFinished(integrationAvailabilityRequest) ||
    (NetworkRequest.areFinished(privacyCenterRequest) && !privacyCenter?.id);

  return {
    integrationAvailability,
    privacyCenterRequest,
    integrationAvailabilityRequest,
    isReady,
  };
};
